import { Directive, ElementRef, HostListener, OnInit, Optional } from '@angular/core';
import { NgControl } from '@angular/forms';
import { AsYouType } from 'libphonenumber-js';

@Directive({
  selector: '[appPhoneFormat]'
})
export class PhoneFormatDirective implements OnInit {

  constructor(
    private el: ElementRef<HTMLInputElement>,
    @Optional() private control: NgControl
  ) { }

  private previousValue = '';

  ngOnInit() {
    if (this.control && this.control.valueAccessor) {
      // Intercept model -> view changes
      const originalWriteValue = this.control.valueAccessor.writeValue.bind(this.control.valueAccessor);
      this.control.valueAccessor.writeValue = (val: any) => {
        if (val) {
          const formatted = this.format(val.toString());
          this.previousValue = formatted;
          originalWriteValue(formatted);
        } else {
          this.previousValue = '';
          originalWriteValue(val);
        }
      };

    }

    // Defer initialization to ensure form control is fully mounted
    setTimeout(() => {
      if (this.control && this.control.control) {
        // 1. Strip any formatting that might have come from the backend initially
        const initialVal = this.control.control.value;
        if (initialVal && typeof initialVal === 'string') {
          const rawInitial = initialVal.replace(/[^\d+]/g, '');
          if (initialVal !== rawInitial) {
            this.control.control.setValue(rawInitial, { emitEvent: false, emitModelToViewChange: false });
          }
        }

        // 2. Intercept future typing to strip formatting
        this.control.control.valueChanges.subscribe(val => {
          if (val && typeof val === 'string') {
            const rawValue = val.replace(/[^\d+]/g, '');
            if (val !== rawValue) {
              this.control.control.setValue(rawValue, { emitEvent: false, emitModelToViewChange: false });
            }
          }
        });
      }

      // 3. Format the initial value for the view (since writeValue might have already been called by Angular before this directive initialized)
      if (this.control && this.control.value) {
        const formatted = this.format(this.control.value.toString());
        this.previousValue = formatted;
        this.el.nativeElement.value = formatted;
      }
    });
  }

  @HostListener('input', ['$event'])
  onInput(event: Event) {
    const inputElement = event.target as HTMLInputElement;
    let value = inputElement.value;

    const oldDigits = this.previousValue.replace(/[^\d+]/g, '');
    let newDigits = value.replace(/[^\d+]/g, '');

    // Cursor position tracking
    let cursorPosition = inputElement.selectionStart || 0;

    // Only treat this as a separator deletion when it is an actual delete event.
    // Otherwise select-all + retype of the same number (raw shorter than formatted)
    // is misread as a backspace and drops the last digit.
    const inputType = (event as InputEvent).inputType || '';
    const isDeletion = inputType.indexOf('delete') !== -1;

    // Detect if a formatting character was deleted (e.g. backspace over a space)
    if (isDeletion && oldDigits === newDigits && value.length < this.previousValue.length) {
      const stringBeforeCursor = value.substring(0, cursorPosition);
      const digitsBeforeCursor = stringBeforeCursor.replace(/[^\d+]/g, '');

      if (digitsBeforeCursor.length > 0) {
        const digitToRemoveIndex = digitsBeforeCursor.length - 1;
        newDigits = newDigits.substring(0, digitToRemoveIndex) + newDigits.substring(digitToRemoveIndex + 1);
        value = newDigits;
      }
    }

    let formatted = this.format(newDigits);

    // Update visual input value
    this.el.nativeElement.value = formatted;

    // Calculate cursor offset
    // We adjust cursor based on how many digits are before the cursor in the original unformatted intent
    const digitsBeforeCursorTarget = newDigits.substring(0, cursorPosition).length; // rough estimate

    // A robust cursor adjustment is to just compare lengths of old formatted vs new formatted
    const diff = formatted.length - value.length;
    let newPos = cursorPosition + diff;
    if (newPos < 0) newPos = 0;

    this.el.nativeElement.setSelectionRange(newPos, newPos);

    this.previousValue = formatted;
  }

  /**
   * Format a raw phone string consistently across the directive.
   * - "+..." international numbers use libphonenumber auto-detection
   * - VN national numbers (starting with 0) are grouped as 4-3-3 (e.g. 0974 363 636)
   * - VN numbers with country code but no "+" (e.g. 84974363636) are treated as +84
   * - everything else falls back to US formatting
   */
  private format(input: string): string {
    if (!input) return '';
    const digits = input.replace(/[^\d+]/g, '');
    if (!digits) return '';

    if (digits.startsWith('+')) {
      return new AsYouType().input(digits);
    }

    if (digits.startsWith('0')) {
      return this.formatVietnamNational(digits);
    }

    if (digits.startsWith('84') && digits.length >= 11) {
      return new AsYouType().input('+' + digits);
    }

    return new AsYouType('US').input(digits);
  }

  /** Group a Vietnamese national number as 4-3-3 (e.g. 0974363636 -> 0974 363 636). */
  private formatVietnamNational(digits: string): string {
    const groups: string[] = [digits.substring(0, 4)];
    for (let i = 4; i < digits.length; i += 3) {
      groups.push(digits.substring(i, i + 3));
    }
    return groups.filter(g => g.length > 0).join(' ');
  }
}
